import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

interface DevicePermission {
  device_id: string
  device_name: string
  can_view: boolean
  can_chat: boolean
  can_edit: boolean
  can_blast: boolean
  can_admin: boolean
}

interface ToolPermission {
  tool_name: string
  can_access: boolean
}

interface FolderGrant {
  folderPath: string
  recursive: boolean
  canUpload: boolean
}

interface UserPermissions {
  devicePermissions: DevicePermission[]
  toolPermissions: ToolPermission[]
  accessibleDevices: string[]
  accessibleTools: string[]
  folderGrants?: FolderGrant[]
}

interface User {
  id: string
  username: string
  fullName?: string
  isAdmin: boolean
  // Enquanto true, o servidor responde 423 em tudo que não seja trocar a
  // senha. A tela precisa saber para mostrar a troca em vez do app quebrado.
  mustChangePassword?: boolean
  permissions?: UserPermissions
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  isLoading: boolean
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>
  reloadPermissions: () => Promise<void>
  marcaSenhaTrocada: () => void
  hasToolAccess: (toolName: string) => boolean
  hasDeviceAccess: (deviceId: string) => boolean
  hasFolderAccess: boolean
  getDevicePermission: (deviceId: string) => DevicePermission | undefined
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Função para carregar permissões do usuário
  const loadUserPermissions = async (authToken: string): Promise<UserPermissions | undefined> => {
    try {
      const response = await fetch(`/api/auth/my-permissions`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      })
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          return data.permissions
        }
      }
    } catch (error) {
      console.error('Erro ao carregar permissões:', error)
    }
    return undefined
  }

  // Verificar token salvo no localStorage ao inicializar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // SSO handoff: outro ambiente (fin/cob) nos mandou o token do CRM no
        // #sso=… ao trocar de ambiente. Como é um token do próprio CRM, adotamos
        // direto: verifica, carrega o user e entra logado. Limpa a URL antes.
        const ssoMatch = window.location.hash.match(/[#&]sso=([^&]+)/)
        if (ssoMatch) {
          const ssoToken = decodeURIComponent(ssoMatch[1])
          history.replaceState(null, '', window.location.pathname + window.location.search)
          try {
            const resp = await fetch('/api/auth/verify', { headers: { Authorization: `Bearer ${ssoToken}` } })
            if (resp.ok) {
              const data = await resp.json()
              const permissions = await loadUserPermissions(ssoToken)
              login(ssoToken, { ...data.user, permissions })
              setIsLoading(false)
              return
            }
          } catch { /* token inválido/rede: segue pro fluxo normal abaixo */ }
        }

        const savedToken = localStorage.getItem('auth-token')
        const savedUser = localStorage.getItem('user-data')

        if (savedToken && savedUser) {
          // Verificar se o token ainda é válido com retry
          let response: Response | null = null
          const maxRetries = 3

          for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
              const controller = new AbortController()
              const timeoutId = setTimeout(() => controller.abort(), 10000) // 10s timeout

              response = await fetch('/api/auth/verify', {
                headers: {
                  'Authorization': `Bearer ${savedToken}`
                },
                signal: controller.signal
              })

              clearTimeout(timeoutId)
              break // Sucesso, sair do loop
            } catch {
              if (attempt < maxRetries - 1) {
                await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000))
              }
            }
          }

          if (response?.ok) {
            const data = await response.json()
            setToken(savedToken)
            const permissions = await loadUserPermissions(savedToken)
            setUser({ ...data.user, permissions })
          } else if (response?.status === 401) {
            // Token realmente inválido — limpar.
            localStorage.removeItem('auth-token')
            localStorage.removeItem('user-data')
            localStorage.removeItem('llm-api-keys')
          } else {
            // 5xx ou sem resposta: manter sessão (transitório).
            const userData = JSON.parse(savedUser)
            setToken(savedToken)
            setUser(userData)
          }
        }
      } catch (error) {
        console.error('Erro ao verificar autenticação:', error)
        // Em caso de erro de parse ou outro, tentar manter sessão se possível
        const savedToken = localStorage.getItem('auth-token')
        const savedUser = localStorage.getItem('user-data')
        if (savedToken && savedUser) {
          try {
            const userData = JSON.parse(savedUser)
            setToken(savedToken)
            setUser(userData)
          } catch {
            // Se não conseguir nem fazer parse, limpar
            localStorage.removeItem('auth-token')
            localStorage.removeItem('user-data')
            localStorage.removeItem('llm-api-keys')
          }
        }
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  const handleLogin = async (username: string, password: string): Promise<boolean> => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          // Carregar permissões do usuário após login
          const permissions = await loadUserPermissions(data.token)
          login(data.token, { ...data.user, permissions })
          return true
        }
      }
      return false
    } catch (error) {
      console.error('Erro no login:', error)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const login = (newToken: string, userData: User) => {
    setToken(newToken)
    setUser(userData)

    // Salvar no localStorage
    localStorage.setItem('auth-token', newToken)
    localStorage.setItem('user-data', JSON.stringify(userData))
  }

  // Depois de trocar a senha o app libera sem relogar: o servidor já lê
  // must_change_password do banco a cada requisição, então o token atual volta
  // a valer para tudo. Persistimos no localStorage porque é de lá que o estado
  // é reidratado num F5.
  const marcaSenhaTrocada = useCallback(() => {
    setUser(prev => {
      if (!prev) return prev
      const novo = { ...prev, mustChangePassword: false }
      try { localStorage.setItem('user-data', JSON.stringify(novo)) } catch { /* modo privado */ }
      return novo
    })
  }, [])

  const logout = useCallback(async () => {
    try {
      // Notificar o servidor sobre o logout
      if (token) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      }
    } catch (error) {
      console.error('Erro ao fazer logout:', error)
    } finally {
      // Limpar estado local
      setToken(null)
      setUser(null)
      
      // Limpar localStorage
      localStorage.removeItem('auth-token')
      localStorage.removeItem('user-data')
      localStorage.removeItem('llm-api-keys')
    }
  }, [token])

  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {})
    }

    // Não adicionar Content-Type se estiver enviando FormData
    // O browser adiciona automaticamente com o boundary correto
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json'
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Retry com backoff exponencial para erros temporários (apenas GET)
    const isPOST = (options as any)?.method?.toUpperCase() === 'POST'
    const maxRetries = isPOST ? 1 : 3 // Não fazer retry em POST (evita duplicação)
    const retryableStatuses = [500, 502, 503, 504]
    let lastError: Error | null = null

    // Signal externo (caller controla cancelamento, ex. AbortController do componente)
    const externalSignal = (options as any)?.signal as AbortSignal | undefined

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const controller = new AbortController()
        const fetchTimeout = (options as any)?.timeout || 30000
        const timeoutId = setTimeout(() => controller.abort(new DOMException('Timeout', 'TimeoutError')), fetchTimeout)

        // Encadeia o signal externo: se o caller abortar, propaga pro fetch.
        // Necessário pra cancelar streams SSE em flight quando o usuário
        // dispara nova request antes da anterior terminar.
        let onExternalAbort: (() => void) | null = null
        if (externalSignal) {
          if (externalSignal.aborted) {
            controller.abort(externalSignal.reason)
          } else {
            onExternalAbort = () => controller.abort(externalSignal.reason)
            externalSignal.addEventListener('abort', onExternalAbort, { once: true })
          }
        }

        const response = await fetch(url, {
          ...options,
          headers,
          signal: controller.signal
        })

        clearTimeout(timeoutId)
        if (externalSignal && onExternalAbort) externalSignal.removeEventListener('abort', onExternalAbort)

        if (response.status === 401) {
          logout()
          throw new Error('Sessão expirada. Faça login novamente.')
        }

        if (retryableStatuses.includes(response.status) && attempt < maxRetries - 1) {
          const delay = Math.pow(2, attempt) * 1000
          await new Promise(resolve => setTimeout(resolve, delay))
          continue
        }

        return response
      } catch (error) {
        lastError = error as Error

        // Cancelamento explícito do caller (signal externo abortado) NUNCA faz retry —
        // o caller pediu pra parar.
        if (externalSignal?.aborted) throw error

        // Retry só em falha real de rede: TypeError de fetch sem resposta,
        // ou abort por timeout do nosso AbortController. NÃO em qualquer
        // mensagem que contenha "fetch" (a antiga heurística era ampla
        // demais e disparava retry em erros já tratados pelo caller).
        const isNetworkError =
          (error instanceof TypeError && /failed to fetch|networkerror/i.test(error.message)) ||
          (error instanceof Error && error.name === 'AbortError')

        if (isNetworkError && attempt < maxRetries - 1) {
          const delay = Math.pow(2, attempt) * 1000
          await new Promise(resolve => setTimeout(resolve, delay))
          continue
        }

        throw error
      }
    }

    throw lastError || new Error('Falha após múltiplas tentativas')
  }, [token, logout])

  // Função para recarregar permissões (útil após mudanças no admin)
  const reloadPermissions = useCallback(async () => {
    if (token) {
      const permissions = await loadUserPermissions(token)
      setUser(prev => prev ? { ...prev, permissions } : prev)
    }
  }, [token])

  // Verificar se o usuário tem acesso a uma ferramenta
  const hasToolAccess = useCallback((toolName: string): boolean => {
    // Admin tem acesso a tudo
    if (user?.isAdmin) return true
    // Verificar nas permissões
    return user?.permissions?.accessibleTools?.includes(toolName) || false
  }, [user])

  // Acesso ao explorer de Arquivos: admin, ou usuário com ao menos 1 concessão de pasta
  const hasFolderAccess = !!(user?.isAdmin || user?.permissions?.folderGrants?.length)

  // Verificar se o usuário tem acesso a um dispositivo
  const hasDeviceAccess = useCallback((deviceId: string): boolean => {
    // Admin tem acesso a tudo
    if (user?.isAdmin) return true
    // Verificar nas permissões
    return user?.permissions?.accessibleDevices?.includes(deviceId) || false
  }, [user])

  // Obter permissão detalhada de um dispositivo
  const getDevicePermission = useCallback((deviceId: string): DevicePermission | undefined => {
    return user?.permissions?.devicePermissions?.find(p => p.device_id === deviceId)
  }, [user])

  const value: AuthContextType = {
    user,
    token,
    login: handleLogin,
    logout,
    isLoading,
    authenticatedFetch,
    reloadPermissions,
    marcaSenhaTrocada,
    hasToolAccess,
    hasDeviceAccess,
    hasFolderAccess,
    getDevicePermission
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider')
  }
  return context
}

// Hook para fazer requisições autenticadas (usa o authenticatedFetch do contexto)
export const useAuthenticatedFetch = () => {
  const { authenticatedFetch } = useAuth()
  return authenticatedFetch
}